/**
 * REST API client for RentOS.
 *
 * Auth strategy:
 *  - The JWT access token is stored in localStorage (scoped per app) and sent
 *    as a Bearer token in the Authorization header on every request.
 *  - This avoids the production cookie collision where both apps share the same
 *    auth_token cookie on .rentos.tech — the last login would overwrite the other.
 *  - The refresh token is still stored as an httpOnly cookie and used by the
 *    /api/auth/refresh endpoint to issue a new access token.
 *
 * Two modes:
 *  - api.*        → standard: on unrecoverable 401, clears token + redirects to /signin
 *  - api.silent.* → silent:   on unrecoverable 401, throws but does NOT redirect/clear
 *                   Use for background fetches (focus-refresh, analytics) where a stale
 *                   session should not force the user out.
 *
 * Token lifetimes:
 *  - Access token:  7 days  (JWT, stored in localStorage)
 *  - Refresh token: 30 days (httpOnly cookie, rotated on each use)
 *  - AuthProvider proactively refreshes every 6 days while the app is open.
 */

function getApiBase(): string {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
  // On native, window.location.origin is https://localhost (the WebView), never the
  // API host — don't fall back to it. Fail loud so a missing env is caught early.
  const nativeCap = typeof window !== "undefined"
    ? (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    : undefined;
  if (nativeCap?.isNativePlatform?.()) {
    throw new Error("VITE_API_BASE_URL is required for the native build.");
  }
  return typeof window !== "undefined" ? window.location.origin : "http://localhost:4000";
}

// ── Token storage ─────────────────────────────────────────────────────────────
// Scoped per app so both apps can run simultaneously without collision.
// Key must match the suffix used in AuthProvider SESSION_KEY.

const TOKEN_KEY =
  import.meta.env.VITE_APP_SCOPE === "platform-admin"
    ? "rentos_adm_token"
    : "rentos_biz_token";

const SESSION_KEY =
  import.meta.env.VITE_APP_SCOPE === "platform-admin"
    ? "rentos_adm_session"
    : "rentos_biz_session";

const REFRESH_KEY =
  import.meta.env.VITE_APP_SCOPE === "platform-admin"
    ? "rentos_adm_refresh"
    : "rentos_biz_refresh";

export function storeAuthToken(token: string): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(TOKEN_KEY, token);
  }
}

export function getStoredAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function clearAuthToken(): void {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(TOKEN_KEY);
    // Also drop any native refresh token so sign-out fully clears the session.
    window.localStorage.removeItem(REFRESH_KEY);
  }
}

// ── Native (Capacitor) refresh-token handling ───────────────────────────────────
// On web, the refresh token lives in an httpOnly cookie (domain=.rentos.tech) and
// the browser sends it automatically. Inside the Capacitor WebView the origin is
// https://localhost / capacitor://localhost, so that cross-site cookie is
// unreliable (iOS WKWebView ITP in particular). On native we therefore persist the
// refresh token returned in auth responses and send it via the X-Refresh-Token
// header on refresh. isNative is derived from the global the native runtime injects,
// so `common` needs no hard dependency on @capacitor/* (the web-only apps don't
// install it).

function isNativePlatform(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.())
  );
}

/** Persist the rotated refresh token (native only; no-op on web). */
export function storeRefreshToken(token: string | null | undefined): void {
  if (!isNativePlatform() || typeof window === "undefined") return;
  if (token) {
    window.localStorage.setItem(REFRESH_KEY, token);
  } else {
    window.localStorage.removeItem(REFRESH_KEY);
  }
}

function getStoredRefreshToken(): string | null {
  if (!isNativePlatform() || typeof window === "undefined") return null;
  return window.localStorage.getItem(REFRESH_KEY);
}

// ── Error class ───────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Subscription event emitter ────────────────────────────────────────────────
// Emits `limit-reached` and `module-blocked` CustomEvents so the UI can react
// globally (e.g. show PlanLimitDialog) without coupling to individual API calls.

export const subscriptionEvents = new EventTarget();

// ── Token refresh ─────────────────────────────────────────────────────────────

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the refresh-token cookie.
 *
 * Exported so callers (e.g. AuthProvider's proactive refresh) share this single
 * implementation, which always targets the real API via getApiBase(). This is
 * required under Capacitor: on native the WebView origin is https://localhost
 * (Android) or capacitor://localhost (iOS), so window.location.origin must NOT
 * be used to build the API URL.
 *
 * Returns the new token (also persisted to storage) or null on failure.
 */
export async function refreshAuthToken(): Promise<string | null> {
  return refreshToken();
}

async function refreshToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Call the API directly — refresh cookie has domain=.rentos.tech so it's
      // sent cross-subdomain. No server-side proxy needed.
      const base = getApiBase();
      // On native, forward the stored refresh token explicitly since the
      // cross-site cookie is unreliable in the WebView.
      const refreshHeaders: Record<string, string> = {};
      const storedRefresh = getStoredRefreshToken();
      if (storedRefresh) refreshHeaders["X-Refresh-Token"] = storedRefresh;

      const res = await fetch(`${base}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: refreshHeaders,
      });
      if (!res.ok) return null;
      const data = await res.json() as { token?: string; refreshToken?: string };
      // Persist the rotated refresh token on native (no-op on web).
      if (data.refreshToken) storeRefreshToken(data.refreshToken);
      if (data.token) {
        storeAuthToken(data.token);
        return data.token;
      }
      return null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ── Core request ──────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
  silent = false,
): Promise<T> {
  const url = `${getApiBase()}${path}`;

  // Build headers — always include the stored JWT as Bearer token
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Only set Content-Type for requests that may have a body
  const method = (options.method || "GET").toUpperCase();
  if (method === "POST" || method === "PUT" || method === "PATCH") {
    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
  }

  const token = getStoredAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    credentials: "include",   // still needed for refresh token cookie
    headers,
  });

  if (res.status === 401 && retry) {
    // Don't treat auth endpoints (login, OTP, password reset) as session expiry
    const isAuthRoute = path.startsWith('/api/auth/');
    if (isAuthRoute) {
      let errorBody: { error?: string; code?: string } = {};
      try { errorBody = await res.json(); } catch { /* ignore */ }
      throw new ApiError(errorBody.error || 'Authentication failed.', 401, errorBody.code || 'AUTH_FAILED');
    }

    const newToken = await refreshToken();
    if (newToken) {
      return request<T>(path, options, false, silent);
    }

    if (!silent && typeof window !== "undefined") {
      clearAuthToken();
      window.localStorage.removeItem(SESSION_KEY);
      const appScope = import.meta.env.VITE_APP_SCOPE ?? "landing";
      const hasSignIn = appScope === "business" || appScope === "platform-admin";
      if (hasSignIn && !window.location.pathname.includes("/signin")) {
        window.location.href = "/signin";
      }
    }
    throw new ApiError("Session expired. Please sign in again.", 401, "UNAUTHENTICATED");
  }

  if (!res.ok) {
    let errorBody: { error?: string; code?: string; resource?: string; limit?: number; used?: number; module?: string } = {};
    try { errorBody = await res.json(); } catch { /* ignore */ }

    // Dispatch subscription-related error events for global UI handling
    if (errorBody.code === 'LIMIT_REACHED') {
      subscriptionEvents.dispatchEvent(new CustomEvent('limit-reached', { detail: errorBody }));
    }
    if (errorBody.code === 'MODULE_NOT_ALLOWED' && !silent) {
      subscriptionEvents.dispatchEvent(new CustomEvent('module-blocked', { detail: errorBody }));
    }

    throw new ApiError(
      errorBody.error || `Request failed with status ${res.status}`,
      res.status,
      errorBody.code,
    );
  }

  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}

// ── Public API ────────────────────────────────────────────────────────────────

export const api = {
  get:    <T>(path: string)                 => request<T>(path, { method: "GET" }),
  post:   <T>(path: string, body?: unknown) => request<T>(path, { method: "POST",   body: body !== undefined ? JSON.stringify(body) : undefined }),
  put:    <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT",    body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch:  <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH",  body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string)                 => request<T>(path, { method: "DELETE" }),

  // Silent variant — 401 throws but never redirects or clears localStorage.
  silent: {
    get:    <T>(path: string)                 => request<T>(path, { method: "GET" },    true, true),
    post:   <T>(path: string, body?: unknown) => request<T>(path, { method: "POST",  body: body !== undefined ? JSON.stringify(body) : undefined }, true, true),
    put:    <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT",   body: body !== undefined ? JSON.stringify(body) : undefined }, true, true),
    patch:  <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined }, true, true),
    delete: <T>(path: string)                 => request<T>(path, { method: "DELETE" }, true, true),
  },
};
